import { useEffect, useRef, useState } from "react";

/**
 * If IntersectionObserver is not supported, this will become a no-op so you need to handle your
 * callback function never being called
 */
const useIntersectionObserver = (
  callback: (
    entries: [IntersectionObserverEntry, ...IntersectionObserverEntry[]],
    observer: IntersectionObserver,
  ) => void,
  options: IntersectionObserverInit = {},
) => {
  const [element, elementRef] = useState<Element | null>(null);
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  const { root, rootMargin, threshold } = options;

  useEffect(() => {
    if (element === null) return () => {};

    const observer = new IntersectionObserver(
      ([entry, ...entries], ...rest) => {
        // The callback will always be called with at least one entry
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        callbackRef.current([entry!, ...entries], ...rest);
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, root, rootMargin, threshold]);

  return [elementRef, element] as const;
};

const intersectionObserverSupported =
  "IntersectionObserver" in window && "IntersectionObserverEntry" in window;

export default intersectionObserverSupported
  ? useIntersectionObserver
  : () => [];
